/* eslint-disable max-len */
import { useCallback, useEffect, useMemo, useState } from "react";
import Axios from "axios";
import { getIntegrationToken } from "../components/helper/api";
import { useSelector } from "react-redux";

export const useUserSubscriptionData = () => {
  const { member: user } = useSelector(state => state);

  const [subscriptionData, setSubscriptionData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  const fetchUserSubscriptions = async () => {
    try {
      setIsLoading(true);
      const token = await getIntegrationToken();

      if (!token) {
        setSubscriptionData([]);
        setHasActiveSubscription(false);
        setIsLoading(false);
        setError("Not able to fetch subscriptions. Please try later");
        return;
      }

      const { data } = await Axios.get(
        `https://www.accesstype.com/api/access/v1/members/me/subscriptions?key=DFQweb8twCUzouLF4EjuRjmU&accesstype_jwt=${token}`
      );

      if (!data) {
        setSubscriptionData([]);
        setHasActiveSubscription(false);
        setIsLoading(false);
        return;
      }

      if (data.subscriptions.length) {
        if (data.subscriptions.find(sub => sub.active)) {
          setHasActiveSubscription(true);
        } else {
          setHasActiveSubscription(false);
        }
      } else {
        setHasActiveSubscription(false);
      }

      setSubscriptionData(data.subscriptions);
      setIsLoading(false);
    } catch (error) {
      setSubscriptionData([]);
      setHasActiveSubscription(null);
      setIsLoading(false);
      setError("Not able to fetch subscriptions. Please try later");
      console.error(error.message);
    }
  };

  const clearState = () => {
    setSubscriptionData([]);
    setHasActiveSubscription(null);
    setError(null);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchUserSubscriptions();
  }, []);

  useEffect(() => {
    if (user) {
      fetchUserSubscriptions();
    } else {
      clearState();
    }
  }, [user]);

  useEffect(() => {
    isGameChangerActive();
    isThreeYearGameChangerActive();
    isDisruptorActive();
    isDisruptorMonthlyActive();
    isDisruptorYearlyActive();
    isActiveSubscriber();
    isRecurringActive();
  }, [subscriptionData]);

  const isGameChangerActive = useCallback(() => {
    if (subscriptionData.length) {
      if (
        subscriptionData.find(
          sub =>
            sub.active === true &&
            sub.group_name.toLowerCase() === "game changer"
        )
      ) {
        return true;
      }

      return false;
    } else {
      return false;
    }
  }, [subscriptionData]);

  const isThreeYearGameChangerActive = useCallback(() => {
    if (subscriptionData.length) {
      if (
        subscriptionData.find(
          sub =>
            sub.active === true &&
            sub.group_name.toLowerCase() === "3 year game changer"
        )
      ) {
        return true;
      }

      return false;
    } else {
      return false;
    }
  }, [subscriptionData]);

  const isDisruptorActive = useCallback(() => {
    if (subscriptionData.length) {
      if (
        subscriptionData.find(
          sub =>
            sub.active === true && sub.group_name.toLowerCase() === "disruptor"
        )
      ) {
        return true;
      }

      return false;
    } else {
      return false;
    }
  }, [subscriptionData]);

  const isActiveSubscriber = useCallback(() => {
    if (subscriptionData.length) {
      if (subscriptionData.find(sub => sub.active === true)) {
        return true;
      }

      return false;
    } else {
      return false;
    }
  }, [subscriptionData]);

  const isDisruptorMonthlyActive = useCallback(() => {
    if (subscriptionData.length) {
      if (
        subscriptionData.find(
          sub =>
            sub.active === true &&
            sub.group_name.toLowerCase() === "disruptor" &&
            sub.duration_length === 1 &&
            sub.duration_unit === "months"
        )
      ) {
        return true;
      }

      return false;
    } else {
      return false;
    }
  }, [subscriptionData]);

  const isDisruptorYearlyActive = useCallback(() => {
    if (subscriptionData.length) {
      if (
        subscriptionData.find(
          sub =>
            sub.active === true &&
            sub.group_name.toLowerCase() === "disruptor" &&
            ((sub.duration_length === 12 && sub.duration_unit === "months") ||
              (sub.duration_length === 1 && sub.duration_unit === "years"))
        )
      ) {
        return true;
      }

      return false;
    } else {
      return false;
    }
  }, [subscriptionData]);

  const isRecurringActive = useCallback(() => {
    if (subscriptionData.length) {
      if (
        subscriptionData.find(
          sub =>
            sub.active === true &&
            ["recurring"].includes(sub.plan_occurrence.toLowerCase())
        )
      ) {
        return true;
      }

      return false;
    } else {
      return false;
    }
  }, [subscriptionData]);

  const isExpiredSubscriber = useCallback(() => {
    if (subscriptionData.length) {
      if (subscriptionData.find(sub => sub.active)) {
        return false;
      } else {
        return true;
      }
    }
    else{
      return false
    }
  }, [subscriptionData]);

  return useMemo(() => {
    return {
      subscriptionData,
      hasActiveSubscription,
      isGameChangerActive,
      isThreeYearGameChangerActive,
      isDisruptorActive,
      isActiveSubscriber,
      isDisruptorMonthlyActive,
      isDisruptorYearlyActive,
      isRecurringActive,
      isExpiredSubscriber,
      isLoading,
      error
    };
  }, [subscriptionData, isLoading, error, hasActiveSubscription]);
};
